import React, { useState, useEffect, useCallback } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Box from '@mui/material/Box';
import { Link as RouterLink } from 'react-router-dom';
import { useMediaQuery, useTheme, Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import './Header.css';

const ADMIN_EMAIL = 'eltonokoth59@gmail.com';

const Header = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [cartDropdownOpen, setCartDropdownOpen] = useState(false);
    const [cartItems, setCartItems] = useState([]);
    const [sessionId, setSessionId] = useState(localStorage.getItem('session_id'));
    const [cartId, setCartId] = useState(localStorage.getItem('cart_id'));
    const [email, setEmail] = useState(localStorage.getItem('email'));
    const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem('phone_number'));
    const [popupOpen, setPopupOpen] = useState(false);
    const [phonePopupOpen, setPhonePopupOpen] = useState(false);
    const [phoneInput, setPhoneInput] = useState('');
    const isAdmin = email === ADMIN_EMAIL;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchSessionInfo = useCallback(async () => {
        try {
            const response = await fetch('https://joshmachapi.onrender.com/api/create-session');
            if (!response.ok) {
                throw new Error('Failed to fetch session information');
            }
            const data = await response.json();
            localStorage.setItem('session_id', data.session_id);
            setSessionId(data.session_id);
        } catch (error) {
            console.error('Error fetching session information:', error);
        }
    }, []);

    const fetchProductDetails = async (productId) => {
        try {
            const response = await fetch(`https://joshmachapi.onrender.com/api/products/details?id=${productId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch product details');
            }
            return response.json();
        } catch (error) {
            console.error('Error fetching product details:', error);
            return null;
        }
    };

    const fetchCartItemsByEmail = useCallback(async (email) => {
        try {
            const order_status = 'pending';
            const response = await fetch(`https://joshmachapi.onrender.com/api/cartItems/email/${email}/status/${order_status}`);
            if (!response.ok) {
                throw new Error('Failed to fetch cart items by email');
            }
            const data = await response.json();
            setCartItems(data);
        } catch (error) {
            console.error('Error fetching cart items by email:', error);
        }
    }, []);

    const fetchCartItemsById = useCallback(async (cartId) => {
        try {
            const response = await fetch(`https://joshmachapi.onrender.com/api/cartItems/${cartId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch cart items by ID');
            }
            const data = await response.json();
            setCartItems(data);
        } catch (error) {
            console.error('Error fetching cart items by ID:', error);
        }
    }, []);

    const toggleDrawer = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const toggleCartDropdown = () => {
        setCartDropdownOpen(!cartDropdownOpen);
    };

    const calculateTotal = () => {
        return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
    };

    const handleOrderNow = async () => {
        if (!email) {
            setPopupOpen(true);
            return;
        }
        if (!phoneNumber) {
            setPhonePopupOpen(true);
            return;
        }
        try {
            let itemNum = 1;
            const orderLines = cartItems.map(item => {
                const line = `${itemNum}. *${item.product_name}*\n   Qty: ${item.quantity} | Price: Ksh ${item.price} | Subtotal: Ksh ${item.price * item.quantity}`;
                itemNum++;
                return line;
            }).join('\n\n');
            const totalAmount = calculateTotal();
            const userName = localStorage.getItem('user_name') || 'Customer';
            const message = `🛒 *NEW ORDER - JoshMach Pharmacy*\n━━━━━━━━━━━━━━━━━━━━\n\n${orderLines}\n\n━━━━━━━━━━━━━━━━━━━━\n💰 *TOTAL: Ksh ${totalAmount}*\n━━━━━━━━━━━━━━━━━━━━\n\n👤 *Customer Details*\nName: ${userName}\nEmail: ${email}\nPhone: ${phoneNumber}\n\n📦 Please confirm availability & delivery. Thank you!`;

            // Open WhatsApp with pre-filled order message
            const whatsappUrl = `https://wa.me/254703650893?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');

            // Update cart items status to 'ordered'
            const statusUpdatePromises = cartItems.map(item => {
                return fetch(`https://joshmachapi.onrender.com/api/cartItems/update-status/id/${item.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ order_status: 'ordered' }),
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to update status for item ${item.id}`);
                    }
                });
            });

            await Promise.all(statusUpdatePromises);
            setCartItems([]);
            setCartDropdownOpen(false);

        } catch (error) {
            console.error('Error placing order:', error);
        }
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    const handleGoogleSuccess = (credentialResponse) => {
        const decoded = jwtDecode(credentialResponse.credential);
        const userEmail = decoded.email;
        const userName = decoded.name;
        localStorage.setItem('email', userEmail);
        localStorage.setItem('user_name', userName);
        setEmail(userEmail);
        setPopupOpen(false);
        // If no phone number saved, ask for it
        const savedPhone = localStorage.getItem('phone_number');
        if (!savedPhone) {
            setPhonePopupOpen(true);
        } else {
            setPhoneNumber(savedPhone);
        }
    };

    const handlePhoneSubmit = () => {
        const cleaned = phoneInput.trim();
        if (!cleaned || cleaned.length < 9) {
            alert('Please enter a valid phone number');
            return;
        }
        localStorage.setItem('phone_number', cleaned);
        setPhoneNumber(cleaned);
        setPhonePopupOpen(false);
    };

    useEffect(() => {
        if (!sessionId) {
            fetchSessionInfo();
        } else {
            if (email) {
                fetchCartItemsByEmail(email);
            } else if (cartId) {
                fetchCartItemsById(cartId);
            }
        }
    }, [sessionId, cartId, email, fetchSessionInfo, fetchCartItemsByEmail, fetchCartItemsById]);

    const cartDropdown = (
        <Box className="cartDropdown">
            <Box className="cartDropdownHeader">
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px' }}>
                    Shopping Cart
                </Typography>
                <IconButton size="small" onClick={toggleCartDropdown} sx={{ color: '#666' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            {cartItems.length === 0 ? (
                <Box className="cartEmpty">
                    <ShoppingCartIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">Your cart is empty</Typography>
                </Box>
            ) : (
            <>
            <List sx={{ p: 0 }}>
                {cartItems.map((item) => (
                    <ListItem key={item.id} className="cartItem" sx={{ px: 0 }}>
                        <img src={item.image_path} alt={item.product_name} className="cartItemImage" />
                        <ListItemText
                            primary={<span className="cartItemName">{item.product_name}</span>}
                            secondary={<span className="cartItemMeta">Qty: {item.quantity} &middot; KSH {item.price}</span>}
                        />
                        <IconButton edge="end" size="small" aria-label="delete" onClick={() => handleDeleteCartItem(item.id)} sx={{ color: '#e53935' }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </ListItem>
                ))}
            </List>
            <Box className="cartFooter">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '15px' }}>
                    Total: <span style={{ color: '#2e7d32' }}>KSH {calculateTotal()}</span>
                </Typography>
                <Button variant="contained" className="whatsappButton" onClick={handleOrderNow} fullWidth>
                    <WhatsAppIcon sx={{ mr: 1 }} /> Order via WhatsApp
                </Button>
            </Box>
            </>
            )}
        </Box>
    );
    const handleDeleteCartItem = async (itemId) => {
        try {
            const response = await fetch(`https://joshmachapi.onrender.com/api/cartItems/${itemId}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error('Failed to delete cart item');
            }
            setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
        } catch (error) {
            console.error('Error deleting cart item:', error);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" className="header">
                <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box className="logoTitle">
                        {isMobile && (
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer(true)}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                            
                        )}
                         <Typography variant="h6" className="title">
                    <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img src={require('../component_photos/logo.jpg')}  alt="Logo" className="logo" />
                    <span>JoshMachPharmacy</span>
                    </RouterLink>
                </Typography>
                <Drawer
    anchor="left"
    open={drawerOpen}
    onClose={toggleDrawer(false)}
    sx={{
        '& .MuiDrawer-paper': {
            background: 'linear-gradient(90deg, #e0eafc, #cfdef3)',
            color: '#333333',
        },
    }}
>
    <Box sx={{ width: 250, height: '100%', overflowY: 'auto' }}  role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
    <IconButton
            edge="start"
            color="inherit"
            aria-label="close"
            onClick={toggleDrawer(false)}
            sx={{
                position: 'absolute',
                top: 10,
                right: 10,
            }}
        >
            <CloseIcon />
        </IconButton>
        <List>
            <ListItem button component={RouterLink} to="/">
                <ListItemText primary="Home" />
            </ListItem>
            <ListItem button component={RouterLink} to="/aboutus">
                <ListItemText primary="About Us" />
            </ListItem>
            <ListItem button component={RouterLink} to="/help">
                <ListItemText primary="Help" />
            </ListItem>
            <ListItem button component={RouterLink} to="/myorders">
                <ListItemText primary="My Orders" />
            </ListItem>
            {isAdmin && (
            <>
            <ListItem button component={RouterLink} to="/adminorders">
                <ListItemText primary="Admin Orders" />
            </ListItem>
            <ListItem button component={RouterLink} to="/addproduct">
                <ListItemText primary="Add Product" />
            </ListItem>
            <ListItem button component={RouterLink} to="/deleteproduct">
                <ListItemText primary="Delete Product" />
            </ListItem>
            </>
            )}
        </List>
        <Box sx={{ position: 'absolute', bottom: 0, width: '100%', display: 'flex', justifyContent: 'space-evenly', padding: '10px 0' }}>
            <IconButton href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer">
                <FacebookIcon />
            </IconButton>
            <IconButton href="https://www.twitter.com/" target="_blank" rel="noopener noreferrer">
                <TwitterIcon />
            </IconButton>
            <IconButton href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer">
                <InstagramIcon />
            </IconButton>
        </Box>
        
    </Box>
</Drawer>

</Box>
{isMobile && (
<IconButton
color="inherit"
className="iconButton"
sx={{ ml: 1 }}
onClick={toggleCartDropdown}
aria-controls="cart-menu"
aria-haspopup="true"
>   
<Badge badgeContent={cartItems.length} color="secondary">
<ShoppingCartIcon />
{cartDropdownOpen && cartDropdown}
</Badge>
</IconButton>)}
{!isMobile && (
<Box className="desktopLinks">
<RouterLink to="/" className="navLink">
Home
</RouterLink>
<RouterLink to="/aboutus" className="navLink">
About Us
</RouterLink>
<RouterLink to="/help" className="navLink">
Help
</RouterLink>
<RouterLink to="/myorders" className="navLink">
    My Orders
</RouterLink>
{isAdmin && (
<>
<RouterLink to="/adminorders" className="navLink">
    <AdminPanelSettingsIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Admin
</RouterLink>
<RouterLink to="/addproduct" className="navLink">
    Add Product
</RouterLink>
<RouterLink to="/deleteproduct" className="navLink">
    Delete Product
</RouterLink>
</>
)}
<Box className="iconContainer">
<IconButton color="inherit" className="iconButton">
<FacebookIcon />
</IconButton>
<IconButton color="inherit" className="iconButton">
<TwitterIcon />
</IconButton>
<IconButton color="inherit" className="iconButton">
<InstagramIcon />
</IconButton>
<IconButton
color="inherit"
className="iconButton"
sx={{ ml: 1 }}
onClick={toggleCartDropdown}
aria-controls="cart-menu"
aria-haspopup="true"
>
    
<Badge badgeContent={cartItems.length} color="secondary">
<ShoppingCartIcon />
</Badge>
</IconButton>
{cartDropdownOpen && cartDropdown}
</Box>
</Box>
)}

</Toolbar>
</AppBar>
<Dialog open={popupOpen} onClose={() => setPopupOpen(false)}>
<DialogTitle sx={{ textAlign: 'center' }}>Sign in to continue</DialogTitle>
<DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
<Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: '#666' }}>
  Sign in with your Google account to shop and track your orders.
</Typography>
<GoogleLogin
  onSuccess={handleGoogleSuccess}
  onError={() => console.error('Google Login Failed')}
  theme="outline"
  size="large"
  width="300"
/>
<Button onClick={() => setPopupOpen(false)} sx={{ mt: 1, color: '#999' }}>Maybe later</Button>
</DialogContent>
</Dialog>
<Dialog open={phonePopupOpen} onClose={() => setPhonePopupOpen(false)}>
<DialogTitle sx={{ textAlign: 'center' }}>Add Your Phone Number</DialogTitle>
<DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2, minWidth: 300 }}>
<Typography variant="body2" sx={{ mb: 1, textAlign: 'center', color: '#666' }}>
  We need your phone number to send your order via WhatsApp.
</Typography>
<TextField
  label="Phone Number"
  placeholder="e.g. 0712345678"
  value={phoneInput}
  onChange={(e) => setPhoneInput(e.target.value)}
  fullWidth
  variant="outlined"
  type="tel"
/>
<Button variant="contained" onClick={handlePhoneSubmit} sx={{ mt: 1, backgroundColor: '#4caf50' }} fullWidth>
  Save & Continue
</Button>
</DialogContent>
</Dialog>
</Box>
);
};

export default Header;